import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginDto } from '../../../dto/auth/login.dto';
import { environment } from '../../../../environments/environment';
import { LoginResponseDto } from '../../../dto/auth/login-response.dto';
import { Observable } from 'rxjs';
import { CambiarContraseniaRequestDto } from '../../../dto/auth/cambiar-contrasenia.request.dto';
import {
  CambiarContraseniaResponseDto,
  RespuestaCambioConstraseniaDTO,
} from '../../../dto/auth/cambiar-contrasenia.response.dto';
import { Constants } from '../../../constants/constants';
import { RestablecerContraseniaRequestDto } from '../../../dto/auth/restablecer-contrasenia.request.dto';

import { VerificarCodigoDobleFactorRequestDto } from '../../../dto/auth/verificar-codigo-doble-factor.request.dto';
import { UtilService } from '../../../services/util.service';
import { UserDataDto } from '../../../dto/auth/user-data.dto';
import { ValidarUsuarioRequestDto } from '../../../dto/auth/validar-usuario.request.dto';
import { ValidarUsuarioResponseDto } from '../../../dto/auth/validar-usuario.response.dto';

@Injectable({
  providedIn: 'root',
})
export class AutenticacionService {
  private isSecure = environment.esHttps;
  private apiUrl = `${environment.apiUrl}/autenticacion`;
  public timer!: ReturnType<typeof setInterval> | null;
  private refreshTokenTimeout: any;

  constructor(
    private utilService: UtilService,
    private http: HttpClient
  ) { }

  public executeRefreshToken(token: string): void {
    console.log("Se realiza el llamado a refresh token");

    const payload: any = this.decodeJwt(token);
    localStorage.setItem('accessTokenExp', payload.exp);

    if (!localStorage.getItem(Constants.LOGIN_TIMESTAMP)) {
      const loginTimestamp = new Date().toISOString();
      localStorage.setItem(Constants.LOGIN_TIMESTAMP, loginTimestamp);
    }
  }

  public scheduleRefreshFromExpiration(exp: number): void {
    // limpiar si ya hay timer
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }

    const expires = exp * 1000;
    const timeout = expires - Date.now() - 60 * 1000;

    this.refreshTokenTimeout = setTimeout(() => {
      const accessToken = sessionStorage.getItem(Constants.ACCESS_TOKEN);
      const refreshToken = sessionStorage.getItem(Constants.REFRESH_TOKEN);
      // Aquí ya SI se obtiene el token desde la cookie (HttpOnly)
      // mediante una llamada refresh:
      if(this.isSecure){
        this.refreshToken("", "");
      }else{
        if(accessToken && refreshToken){
          this.refreshToken(accessToken, refreshToken);
        }
      }
    }, timeout > 0 ? timeout : 0);
  }

  public stopRefreshToken(): void {
    clearTimeout(this.refreshTokenTimeout);
  }

  private decodeJwt(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      return {};
    }
  }

  private refreshToken(token: string, refreshToken: string) {
    console.log("ingresa a refreshToken")
    try {
      const usuarioString = sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}';
      const usuario: UserDataDto = JSON.parse(usuarioString);
      const idUsuario = Number(usuario?.idUsuario);

      this.renovarToken({ idUsuario, refreshToken }).subscribe({
        next: (data) => {
          if (data.status === 'SUCCESS') {
            if (!this.isSecure) {
              sessionStorage.setItem(Constants.ACCESS_TOKEN, data.access_token);
              sessionStorage.setItem(Constants.REFRESH_TOKEN, data.refresh_token);
            }

            const decoded: any = this.decodeJwt(data.access_token);
            const nuevaExp = decoded?.exp;

            this.scheduleRefreshFromExpiration(nuevaExp);

            this.executeRefreshToken(data.access_token);
          } else {
            this.logout();
          }
        },
        error: (err) => {
          console.error('[REFRESH TOKEN] Error en la petición renovarToken:', err);
          this.logout();
        },
        complete: () => {
          console.log('%c[REFRESH TOKEN] → Proceso completado.');
        },
      });
    } catch (error) {
      console.error('[REFRESH TOKEN] Error inesperado al procesar el token:', error);
      this.logout();
    }
  }



  public logout() {
    try {
      if (this.timer) {
        clearInterval(this.timer);
      }
    } catch (e) {
    }
    const usuarioString =
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}';
    const usuario: UserDataDto = JSON.parse(usuarioString);
    const idUsuario = Number(usuario?.idUsuario);
    if (idUsuario > 0) {
      this.utilService.logout(idUsuario).subscribe(data => {
      });
    }
    sessionStorage.removeItem(Constants.USER_INFORMATION_STORAGE);
    if (!this.isSecure) {
      sessionStorage.removeItem(Constants.ACCESS_TOKEN);
      sessionStorage.removeItem(Constants.REFRESH_TOKEN);
    }
    sessionStorage.removeItem(Constants.IS_SUCCESS_LOGIN);
    sessionStorage.removeItem(Constants.FOOTER_INFORMATION_STORAGE);
    this.utilService.deleteCookie(Constants.MDJ_ACCESS_TOKEN);
    localStorage.clear();
    sessionStorage.clear();
    this.utilService.setRole(null);
    this.utilService.hideNavUser();
  }

  login(data: LoginDto): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(`${this.apiUrl}/login`, data);
  }

  loginDigital(tokenDigital: string, idToken?: string): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(`${this.apiUrl}/loginDigital`, {
      tokenDigital,
      ...(idToken && { idToken }),
    });
  }

  renovarToken(data: LoginDto): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(
      `${this.apiUrl}/renovarToken`,
      data
    );
  }

  cambiarConstrasenia(
    token: string | undefined,
    data: CambiarContraseniaRequestDto
  ): Observable<CambiarContraseniaResponseDto> {
    const headers = {
      Authorization: '',
      MDJ_TOKEN_AUTH:
        this.utilService.getCookie(Constants.MDJ_ACCESS_TOKEN) ?? '',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return this.http.post<CambiarContraseniaResponseDto>(
      `${this.apiUrl}/cambiar-contrasena`,
      data,
      {
        headers,
        withCredentials: token === undefined,
      }
    );
  }

  restablecerConstraseniaCorreoElectronico(
    email: string
  ): Observable<CambiarContraseniaResponseDto> {
    return this.http.post<CambiarContraseniaResponseDto>(
      `${this.apiUrl}/restablecer-contrasena-email`,
      { email: email }
    );
  }

  restablecerConstrasenia(
    usuarioId: string,
    data: RestablecerContraseniaRequestDto
  ): Observable<CambiarContraseniaResponseDto> {
    return this.http.post<CambiarContraseniaResponseDto>(
      `${this.apiUrl}/restablecer-contrasena/${usuarioId}`,
      data
    );
  }

  verificarCodigoDobleFactor(
    usuarioId: string,
    data: VerificarCodigoDobleFactorRequestDto
  ): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(
      `${this.apiUrl}/verificar-codigo-doble-factor/${usuarioId}`,
      data
    );
  }

  actualizarContrasena(): Observable<any> {
    return this.http.get(`${this.apiUrl}/actualizar-contrasena`);
  }
}
